import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router';
import { skipToken } from '@reduxjs/toolkit/query';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import { CLOSE_DIALOG_EVENT } from '@rio-cloud/rio-uikit/FrameDialog';
import usePostMessageSender from '@rio-cloud/rio-uikit/usePostMessageSender';

import { MFE_DIALOG_PREFIX } from '@/features/app/dialogs/dialogConstants';
import DialogErrorState from '@/features/app/dialogs/DialogErrorState';
import DialogFooter from '@/features/app/dialogs/DialogFooter';
import DialogLoadingState from '@/features/app/dialogs/DialogLoadingState';
import { useGetWorkshopQuery } from '@/services/api';

const renderAddressLine = (text?: string) => (text ? <div>{text.trim()}</div> : '-');

const WorkshopDetailsDialog = () => {
    const { workshopId } = useParams<{ workshopId: string }>();
    const { sendMessageToParent } = usePostMessageSender();

    const { data: workshop, isLoading, isError, refetch, isFetching } = useGetWorkshopQuery(workshopId ?? skipToken);

    const handleCloseDialog = () => {
        sendMessageToParent({
            type: `${MFE_DIALOG_PREFIX}${CLOSE_DIALOG_EVENT}`,
            payload: `Closed workshop details dialog for workshop ID: ${workshopId}`,
        });
    };

    if (isLoading || isFetching) {
        return <DialogLoadingState onClose={handleCloseDialog} />;
    }

    if (isError || !workshop) {
        return <DialogErrorState onClose={handleCloseDialog} refetch={refetch} />;
    }

    const { workshop_address: address } = workshop;
    const countryCode = address?.country_code ?? '';

    return (
        <div className="bg-white min-height-200">
            <Dialog.Title title={workshop.name} onCloseButtonClick={handleCloseDialog} />

            <Dialog.Body className="padding-20">
                {renderAddressLine(address?.street ?? undefined)}
                {address?.zip && address?.city
                    ? `${address.zip.trim()} ${address.city.trim()}`
                    : renderAddressLine(address?.city ?? undefined)}
                {countryCode && (
                    <div>
                        <FormattedMessage id={`intl-msg:common.country.${countryCode}`} defaultMessage={countryCode} />
                    </div>
                )}
            </Dialog.Body>

            <DialogFooter onClose={handleCloseDialog} />
        </div>
    );
};

export default WorkshopDetailsDialog;
